import * as React from 'react';

import {RouteComponentProps, withRouter} from 'react-router';
import {compose, Dispatch} from "redux";
import {translate} from "react-i18next";
import {connect} from "react-redux";
import {ITranslatedComponent, ITypefamilyMap} from '../../../redux/reducers/generalReducer';
import {Input, Checkbox, Table} from 'semantic-ui-react';
import {ESubscriptionType, IPslEmailSubscription} from '../../../data/IPslEmailSubscription';
import {Button} from '../../Button';
import {Dialog} from '../Dialog';
import {IUserSettings} from "../../../data/IUserSettings";
import {CnrDto} from "../../../services/Client";
import GeneralActions from "../../../redux/actions/GeneralActions";
import {
    selectBasicMarketCnr,
    selectTypefamilyMap,
    selectUserSettings,
    selectUserSubscription
} from '../../../redux/selectors';
import {IReduxState} from 'src/redux/IReduxState';

interface IMapDispatchToProps {
    saveUserSettings: (userSettings: IUserSettings) => void;
}

interface IMapStateToProps {
    typefamilies?: ITypefamilyMap;
    userEmailSubscriptions: {[key: string]: ESubscriptionType[]};
    userSettings: IUserSettings;
    basicMarketCnr: CnrDto | undefined;
}

interface IOwnProps {
    onClose: () => void;
}

interface EmailSubscriptionDialogState {
    userEmailSubscriptions: {[key: string]: ESubscriptionType[]};
    onlyMySubscriptions: boolean;
    filteredValues: {
        modelyear: string,
        typefamily: string
    };
}

interface EmailSubscriptionDialogProps extends IMapStateToProps, IMapDispatchToProps, IOwnProps, ITranslatedComponent, RouteComponentProps<any> {

}

class EmailSubscriptionDialog extends React.Component<EmailSubscriptionDialogProps, EmailSubscriptionDialogState> {
    constructor(props: EmailSubscriptionDialogProps) {
        super(props);
        this.state = {
            userEmailSubscriptions: {},
            onlyMySubscriptions: false,
            filteredValues: {
                modelyear: "",
                typefamily: ""
            },
        };
    }

    componentDidMount(): void {
        if (this.props.userEmailSubscriptions) {
            this.setState({
                userEmailSubscriptions: this.props.userEmailSubscriptions
            });
        }
    }

    componentDidUpdate(prevProps: EmailSubscriptionDialogProps) {
        if (prevProps.userSettings !== this.props.userSettings) {
            this.setState({
                userEmailSubscriptions: this.props.userEmailSubscriptions
            });
        }
    }

    onChange = (data: any, rowId: string, value: ESubscriptionType) => {
        const newSubscriptions = {...this.state.userEmailSubscriptions};

        if (this.state.userEmailSubscriptions[rowId]) {
            if (value === ESubscriptionType.ALL) {
                if (data.checked) {
                    newSubscriptions[rowId] = [value];
                } else {
                    delete newSubscriptions[rowId];
                }
            } else {
                if (data.checked) {
                    newSubscriptions[rowId] = [...newSubscriptions[rowId], value].filter(sub => sub !== ESubscriptionType.ALL);
                } else {
                    newSubscriptions[rowId] = newSubscriptions[rowId].filter(sub => sub !== value);
                    if (newSubscriptions[rowId].length === 0) {
                        delete newSubscriptions[rowId];
                    }
                }
            }
        } else {
            newSubscriptions[rowId] = [value];
        }

        this.setState({
            userEmailSubscriptions: newSubscriptions
        });
    }

    onSave = () => {
        const userSubscription: IPslEmailSubscription[] = [];
        Object.keys(this.state.userEmailSubscriptions).map(sub => {
            userSubscription.push({
                psl: sub,
                subscriptionType: this.state.userEmailSubscriptions[sub]
            });
        });

        const userSettings = {...this.props.userSettings};
        userSettings.pslEmailSubscriptions = userSubscription;
        this.props.saveUserSettings(userSettings);
        this.setState({
            filteredValues: {
                modelyear: "",
                typefamily: "",
            },
            onlyMySubscriptions: false,
        });
    }

    onInputChange = (e: React.ChangeEvent<HTMLInputElement>, {name, value}: {name: "modelyear" | "typefamily", value: string}) => {
        this.setState({
            filteredValues: {
                ...this.state.filteredValues,
                [name]: value
            }
        });
    }

    capitalizeFirstLetter(str: string) {
        return str.charAt(0).toUpperCase() + str.slice(1).toLocaleLowerCase();
    }

    render() {
        const {t} = this.props;
        return (
            <Dialog
                customStyleClassName="email-subscription-dialog-container"
                open={true}
                fullWidth={true}
                title={t("PSL_SUBS:PSL_SUBS_DIALOG_TITLE")}
                onRequestClose={this.props.onClose}
                actions={
                    [
                        <Button
                            key={"cancelButton"}
                            icon={"ban"}
                            onClick={this.props.onClose}
                        />
                    ]
                }
            >
                <React.Fragment>
                    <p style={{fontSize: 16}}>{t("PSL_SUBS:PSL_SUBS_DISCLAIMER")}</p>

                    <div className="email-subscription-dialog-header-actions">
                        <Checkbox
                            toggle={true}
                            checked={this.state.onlyMySubscriptions}
                            label={t("PSL_SUBS:ONLY_MY_PSL_SUBS")}
                            onChange={() => {
                                this.setState({
                                    onlyMySubscriptions: !this.state.onlyMySubscriptions
                                });
                            }}
                        />
                        <Button
                            style={{marginRight: 0}}
                            label={t("GLOBAL:SAVE")}
                            onClick={this.onSave}
                            icon="save"
                        />
                    </div>
                    <div className="email-subscription-dialog-table-container">
                        <Table
                            id="emailSubscriptionTable"
                            textAlign='center'
                            verticalAlign='middle'
                            celled={true}
                        >
                            <Table.Header>
                                <Table.Row className="email-subscription-dialog-table-first-row">
                                    <Table.HeaderCell width={2} rowSpan='2'>{t("GLOBAL:C_NUMBER")}</Table.HeaderCell>
                                    <Table.HeaderCell width={2}>{t("GLOBAL:MODELYEAR")}</Table.HeaderCell>
                                    <Table.HeaderCell width={3}>{t("GLOBAL:TYPEFAMILY")}</Table.HeaderCell>
                                    <Table.HeaderCell rowSpan='2' width={2}>{t("PSL_SUBS:PSL_SUBS_REJECTION")}</Table.HeaderCell>
                                    <Table.HeaderCell rowSpan='2' width={2}>{t("PSL_SUBS:PSL_SUBS_APPROVAL")}</Table.HeaderCell>
                                    <Table.HeaderCell rowSpan='2' width={2}>{t("GLOBAL:ALL")}</Table.HeaderCell>
                                </Table.Row>
                                <Table.Row className="email-subscription-dialog-table-second-row">
                                    <Table.HeaderCell width={2} style={{borderLeft: "1px solid rgba(34,36,38,.1)"}}>
                                        <Input
                                            value={this.state.filteredValues.modelyear}
                                            name="modelyear"
                                            fluid={true}
                                            onChange={this.onInputChange}
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell width={3}>
                                        <Input
                                            value={this.state.filteredValues.typefamily}
                                            name="typefamily"
                                            fluid={true}
                                            onChange={this.onInputChange}
                                        />
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {this.props.typefamilies
                                    && this.props.basicMarketCnr
                                    && this.props.basicMarketCnr.objectId
                                    && this.props.basicMarketCnr.cnr
                                    && Object.keys(this.props.typefamilies).map(tf => {
                                        // @ts-ignore
                                        return this.props.typefamilies[tf].elements.map(elem => {
                                            // @ts-ignore
                                            const rowId = `${this.props.basicMarketCnr.objectId}/${elem.modelyear && elem.modelyear.objectId}/${elem.objectId}`;
                                            const subscription = this.state.userEmailSubscriptions[rowId];

                                            if (this.state.onlyMySubscriptions && (!subscription || subscription.length === 0)) {
                                                return null;
                                            }
                                            const isFiltered = elem.name.toLowerCase().includes(this.state.filteredValues.typefamily.toLowerCase())
                                                && elem.modelyear && elem.modelyear.mj4.toString().toLowerCase().includes(this.state.filteredValues.modelyear);
                                            if (!isFiltered) {
                                                return null;
                                            }

                                            return (
                                                <Table.Row key={tf + elem.name}>
                                                    <Table.Cell>
                                                        {
                                                            // @ts-ignore
                                                            this.capitalizeFirstLetter(this.props.basicMarketCnr.cnr)
                                                        }
                                                    </Table.Cell>
                                                    <Table.Cell>{elem.modelyear && elem.modelyear.mj4}</Table.Cell>
                                                    <Table.Cell>{elem.name}</Table.Cell>
                                                    <Table.Cell>
                                                        <Checkbox
                                                            className="email-subscription-dialog-checkbox"
                                                            checked={subscription ? subscription.includes(ESubscriptionType.REJECTED) : false}
                                                            onChange={(event: React.SyntheticEvent, data: object) => this.onChange(data, rowId, ESubscriptionType.REJECTED)}
                                                        />
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        <Checkbox
                                                            className="email-subscription-dialog-checkbox"
                                                            checked={subscription ? subscription.includes(ESubscriptionType.APPROVED) : false}
                                                            onChange={(event: React.SyntheticEvent, data: object) => this.onChange(data, rowId, ESubscriptionType.APPROVED)}
                                                        />
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        <Checkbox
                                                            className="email-subscription-dialog-checkbox"
                                                            checked={subscription ? subscription.includes(ESubscriptionType.ALL) : false}
                                                            onChange={(event: React.SyntheticEvent, data: object) => this.onChange(data, rowId, ESubscriptionType.ALL)}
                                                        />
                                                    </Table.Cell>
                                                </Table.Row>
                                            );

                                        });
                                    })
                                }
                            </Table.Body>
                        </Table>
                    </div>
                </React.Fragment>
            </Dialog>
        );
    }
}

const mapStateToProps = (state: IReduxState): IMapStateToProps => ({
    typefamilies: selectTypefamilyMap(state),
    userEmailSubscriptions: selectUserSubscription(state),
    userSettings: selectUserSettings(state),
    basicMarketCnr: selectBasicMarketCnr(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>, ownProps: EmailSubscriptionDialogProps): IMapDispatchToProps => ({
    saveUserSettings: (userSettings: IUserSettings) => dispatch(GeneralActions.SaveUserSettings(userSettings, ownProps.t)),
});

export default compose<React.ComponentType<IOwnProps>>(
    withRouter,
    translate(["PSL_SUBS", "GLOBAL"]),
    connect<IMapStateToProps, IMapDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(EmailSubscriptionDialog);



// WEBPACK FOOTER //
// ./src/components/Dialogs/emails-subscription-dialog/EmailSubscriptionDialog.tsx